<template>
    <div class="order-item" :class="{ 'has-discount': item.hasDiscount }">
        <OrderItemQuantity class="quantity" :quantity="item.quantity" :quantityDigits="quantityDigits" />

        <div class="item-details-container">
            <div class="main-details">
                <span class="name">{{ getNameInLocationLanguage(item, props.showInternalNames) }}<ItemIcons v-if="item.articleId" :articleId="item.articleId" /></span>
                <ItemPrice
                    :hasDiscount="item.hasDiscount"
                    :quantity="item.quantity"
                    :unitPrice="item.unitPrice"
                    :unitPriceAfterDiscount="item.unitPriceAfterDiscount"
                    :discountType="item.lastDiscountType"
                />
            </div>

            <div class="subs" v-if="item.options.length">
                <OrderItemOptionGroup
                    v-for="optionGroup in item.options"
                    :key="optionGroup.id"
                    :optionGroup="optionGroup"
                    :multiplier="item.quantity"
                    :showInternalNames="props.showInternalNames"
                    :alwaysShowGroupName="props.alwaysShowGroupName"
                />
            </div>

            <div class="comment" :class="{ 'operator-comment': props.isWaiterOrAdmin }" v-if="item.comment">
                <div class="comment-text">{{ item.comment.text }}</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { IOrderItem } from "@/data/model/Order";
    import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";

    import ItemIcons from "@/components/order/ItemIcons.vue";
    import ItemPrice from "@/components/order/ItemPrice.vue";
    import OrderItemOptionGroup from "@/components/order/OrderItemOptionGroup.vue";
    import OrderItemQuantity from "@/components/order/OrderItemQuantity.vue";

    const props = defineProps<{
        item: IOrderItem;
        quantityDigits?: number;
        showInternalNames: boolean;
        isWaiterOrAdmin: boolean;
        alwaysShowGroupName?: boolean;
    }>();
</script>

<style scoped lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .order-item {
        font-size: 16px;
        padding: 1.25em 0;
        position: relative;
        border-bottom: 1px solid colors.$lightGreyBorder;
        font-weight: 300;
        line-height: 1.25em;
        color: colors.$fontColor;
        display: flex;

        &.has-discount {
            padding-top: 1.725em;

            .main-details {
                margin-bottom: -0.625em;
            }
        }

        > .item-details-container {
            flex: 1 1 auto;
            color: colors.$fontColor;
            transition: color 0.2s variables.$easeOutExpo;

            &.inactive {
                color: colors.$lightFontColor;
            }

            > .main-details {
                display: flex;
                align-items: flex-start;

                .name {
                    text-overflow: ellipsis;
                    flex: 1 1 auto;
                    word-break: break-word;
                }

                .price {
                    flex: 0 0 auto;

                    &.has-discount {
                        position: relative;
                        top: -0.625em;
                    }
                }
            }

            > .subs {
                padding: 0.375em 0.125em 0.5em;
                margin-bottom: -0.75em;
            }

            > .comment {
                padding-top: 6px;
                font-style: italic;
                font-size: 12px;
                color: colors.$lightFontColor;

                &.operator-comment {
                    font-weight: bold;
                    color: colors.$red;
                }
            }
        }
    }
</style>
